import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  signal, OnDestroy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  trigger,
  state,
  style,
  animate,
  transition,
} from '@angular/animations';
import { MAT_TOOLTIP_DEFAULT_OPTIONS} from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { UserService, User } from '../../../core/services/user.service';
import {
  AccountService,
  ChangeVersionAndTeam,
} from '../../../core/services/account.service';
import { LayoutState } from '../../../app.component';
import { AppTenantFlag } from '../../models/app-tenant-flag.enum';
import { PlatformEnum } from '../../models/platform-enum';
import {ActionMenuButtonComponent, ActionMenuButtonItem} from '../action-menu-button';
import { NoteType } from '../../models/note-type.enum';
import { NoteLink, PermissionEntity } from '../../models/note.model';
import { EntityType } from '../../models/entity-type.enum';
import { propertyLink } from '../../util/appUrl.file';
import { environment } from '../../../../environments/environment';
import { AssetUpdatePayload } from '../../../add-entity/models/add-property.model';
import { AppNotificationService } from '@shared/services/app-notification.service';
import { SidenavService } from '@shared/services/sidenav.service';
import {BoxComponent} from '@shared/components/box/box.component';
import {MatButtonModule} from '@angular/material/button';
import { AsyncPipe } from '@angular/common';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatGridListModule} from '@angular/material/grid-list';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatDividerModule} from '@angular/material/divider';
import {AppNotificationsComponent} from '@shared/components/app-notifications/app-notifications.component';
import {AddContactComponent} from '../../../add-entity/components/add-contact/add-contact.component';
import {AddPropertyDialogService} from '../../../add-entity/components/add-property/add-property.dialog';
import {
  AddActivityDeprecatedDialogComponent
} from '@shared/components/notes/add-activity/add-activity-deprecated-dialog.component';
import {GlobalSearchComponent} from '@shared/components/global-search/global-search.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AddNewCompanyDialogComponent } from '@shared/components/add-new-company-dialog/add-new-company-dialog.component';
import { provideTranslocoScope, TranslocoPipe } from '@jsverse/transloco';

export interface AddNewOptions {
  displayValue: string;
  value: string | null;
  deprecated: boolean;
}

const addNewOptions: AddNewOptions[] = [
  { displayValue: 'Deal', value: 'deal', deprecated: true },
  { displayValue: 'Contact', value: `${environment.baseUrl}/contacts`, deprecated: false },
  { displayValue: 'Company', value: 'company2', deprecated: false },
  { displayValue: 'Property', value: 'property', deprecated: true },
  { displayValue: 'Deal Activity', value: 'note', deprecated: false },
];

export interface IdealVersion {
  id: number;
  name: string;
}

const versions: IdealVersion[] = [
  { id: 1, name: 'Ideal 1.0' },
  { id: 2, name: 'Ideal 2.0' },
];

@Component({
    selector: 'app-header',
    templateUrl: './app-header.component.html',
    styleUrls: ['./app-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('slideInOut', [
            state('in', style({
                transform: 'translate3d(0,0,0)',
            })),
            state('out', style({
                transform: 'translate3d(100%, 0, 0)',
            })),
            transition('in => out', animate('400ms ease-in-out')),
            transition('out => in', animate('400ms ease-in-out')),
        ]),
    ],
    providers: [{
            provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
            useValue: {
                position: 'left',
            },
        }, provideTranslocoScope('sharedNav')],
    imports: [
    BoxComponent,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatGridListModule,
    MatInputModule,
    MatSelectModule,
    MatDividerModule,
    AppNotificationsComponent,
    AddContactComponent,
    AddActivityDeprecatedDialogComponent,
    ActionMenuButtonComponent,
    GlobalSearchComponent,
    AsyncPipe,
    MatDialogModule,
    TranslocoPipe
]
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  private userSubscription?: Subscription;
  private userDefaultsSubscription?: Subscription;

  @Input() public layoutState: LayoutState = { isIconView: false, isMobileView: false, isMobileMenuOpen: false };

  @Output() public onNavToggle: any = new EventEmitter();
  @Output() public onSelectTeam: any = new EventEmitter();
  @Output() public onLogout: any = new EventEmitter();
  @Output() public onThemeToggled: any = new EventEmitter();

  public readonly actionMenuButtonItems: ActionMenuButtonItem[] = [];
  public readonly open= signal(false);

  public user!: User;
  public userMenuActive!: boolean;
  public defaultsPanelActive!: boolean;
  public selectedDefaultVersionId!: number;
  public selectedDefaultTeamId?: number;

  public versions: IdealVersion[] = versions;
  /** This application is Ideal 2.0; the dropdown only leaves when the user picks 1.0. */
  public selectedVersionId = AppTenantFlag.CommercialCM;
  public showVersionField!: boolean;
  public defaultTeams!: any[];
  public activeTeams!: any[];
  public isDebtUser!: boolean;

  public showAddContactPopup = false;
  public showAddActivityModal = false;
  public noteType = NoteType.DealMarketing;
  public modalTitle = 'Add Activity';
  public linkDeals: NoteLink[] = [];
  public permissions: PermissionEntity[] = [];
  public entityType = EntityType;
  public platform!: number;

  public isLowerEnvironment= true;
  public canViewNotificationCenter = false;

  public constructor(
    private userService: UserService,
    private accountService: AccountService,
    private changeDetector: ChangeDetectorRef,
    private router: Router,
    public notificationService: AppNotificationService,
    private sidenavService: SidenavService,
    private dialog: MatDialog,
    private addPropertyDialogService: AddPropertyDialogService,
  ) {
    addNewOptions.forEach((item: AddNewOptions) => {
      this.actionMenuButtonItems.push({
        label: item.displayValue,
        trackingClass: 'matomo_header',
        onClickAction: () => this.onActionMenuitemClickEvent(item),
      });
    });
    this.isLowerEnvironment= Boolean(JSON.parse(environment.isLowerEnvironment));

    this.sidenavService.isOpenSideBar$.subscribe((isOpen: boolean) => {
      this.isOpenChange(isOpen);
    });
  }

  public onActionMenuitemClickEvent(item: AddNewOptions) {
    switch (item.displayValue.toLocaleLowerCase()) {
      case 'deal':
        this.router.navigate(['/add-entity/deal']);
        break;
      case 'company':
        this.openAddNewCompanyModal()
        break;
      case 'property':
        this.openPropertyDialog();
        break;
      case 'contact':
        this.openDialog();
        break;
      case 'deal activity':
        this.openDealActivityDialog();
        break;
    }
  }

  public ngOnInit(): void {
    this.userSubscription = this.userService.get()
      .subscribe((user): void => {
        this.user = user;
        this.selectedDefaultVersionId = user.defaultTenant;
        this.selectedDefaultTeamId = user.defaultTeamId!;
        this.defaultTeams = user.teams
          .filter(t => t.appTenantFlag === AppTenantFlag.All
            || t.appTenantFlag === this.selectedDefaultVersionId);

        this.defaultTeams = this.userService.getTeamDropdownOptions(this.user.teams);
        this.activeTeams = this.userService.getTeamDropdownOptions(this.user.teams);
        this.showVersionField = user.tenantOptions === AppTenantFlag.All;
        this.platform =  this.user.activeTeamPlatform;
        this.isDebtUser =  this.platform === PlatformEnum.Debt2
        || this.platform === PlatformEnum.DebtCommercial
        || this.platform === PlatformEnum.Debt;

        this.canViewNotificationCenter = user.canViewInAppNotifications;
        this.notificationService.isEnableNotificationBellIcon.next(user.hasUnreadInAppNotifications);
      });
  }

  public ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
    this.userDefaultsSubscription?.unsubscribe();
  }

  public getActiveTeamName(): string {
    const activeTeam = this.user.teams
      .find((team): boolean => team.id === this.user.activeTeamId);
    return activeTeam ? `${activeTeam.name}` : `${this.user.name}`;
  }

  public setTeam(id: number): void {
    this.accountService.setTeam(id);
  }

  public setVersion(id: number): void {
    this.accountService.setVersion(id);
  }

  public toggleSidebar(): void {
    this.onNavToggle.emit();
  }

  public openDialog() {
    this.showAddContactPopup = true;
    this.changeDetector.detectChanges();
  }

  public openPropertyDialog() {
    void this.addPropertyDialogService.showAddPropertyDialog({
      from: 'header',
    }).then((payload) => {
      if (payload) {
        this.onSaveAsset(payload);
      }
      this.changeDetector.markForCheck();
    });
  }

  public toggleNotificationsSidebar(): void{
    this.open.set(!this.open());
    this.changeDetector.detectChanges();
  }

  public isOpenChange(isopen: boolean)
  {
    this.open.set(isopen);
  }

  public openDealActivityDialog() {
    this.showAddActivityModal = true;
    this.changeDetector.detectChanges();
  }
  public openAddNewCompanyModal() {
    this.dialog.open(AddNewCompanyDialogComponent, {
      width: '800px',
      disableClose: true,
    });
  }

  public toggleDefaultsPanel(showPanel: boolean): void {
    this.defaultsPanelActive = showPanel;
    this.changeDetector.detectChanges();
  }

  public openPreferences(): void {
    this.router.navigate(['/preferences']);
  }

  public setVersionAndTeam(): void {
    const payload: ChangeVersionAndTeam = {
      selectedAppTenantId: this.selectedDefaultVersionId,
      selectedTeamId: this.selectedDefaultTeamId,
      setTenantDefault: true,
      setTeamDefault: true,
    };
    this.accountService.setVersionAndTeam(payload);
  }

  public onVersionChange(): void {
    this.defaultTeams = this.user.teams
      .filter(t => t.appTenantFlag === this.selectedDefaultVersionId);

    this.defaultTeams = this.userService.getTeamDropdownOptions(this.defaultTeams);
  }

  public logout(): void {
    this.onLogout();
  }

  public onSaveAsset = ({assetId}: AssetUpdatePayload) => {
    if (assetId && assetId > 0) {
      window.location.href = propertyLink(assetId);
    }
  };
}
